import { type BillingStructure, type RateMultiplier } from '@/types';

/** Live form values passed from MultiplierDetailSheet → MultiplierList → MultiplierListItem for live-updating headers */
export interface MultiplierLiveValues {
  name: string;
  rate: number | null;
  shiftEnabled: boolean;
  shiftThreshold: number | null;
  weeklyEnabled: boolean;
  weeklyThreshold: number | null;
}

/** Minimal role shape needed by suggestion utilities — accepts both full Role and the lighter prop type from MultiplierDetailSheet */
export type RoleInfo = {
  billingStructure: BillingStructure | string | null;
  name?: string | null;
  rate?: number | null;
};

export function decimalToHHMM(decimalHours: number | null | undefined): string {
  if (decimalHours === null || decimalHours === undefined || isNaN(decimalHours)) {
    return '';
  }
  
  // For grid display, use simple formatting without rounding
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
}

export function decimalToHHMMRounded(decimalHours: number | null | undefined): string {
  if (decimalHours === null || decimalHours === undefined || isNaN(decimalHours)) {
    return '';
  }
  // Round up to nearest 15-minute increment
  const totalMinutes = Math.ceil(decimalHours * 4) * 15;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');
  return `${formattedHours}:${formattedMinutes}`;
}

export function HHMMToDecimal(timeString: string | null | undefined): number | null {
  if (!timeString || !timeString.trim()) {
    return null;
  }
  const time = timeString.trim();
  
  if (time.includes(':')) {
    const [hours, minutes] = time.split(':').map(part => parseInt(part, 10));
    if (isNaN(hours) || isNaN(minutes) || minutes < 0 || minutes >= 60) return null;
    return hours + minutes / 60;
  } else {
    const decimalHours = parseFloat(time);
    if (isNaN(decimalHours)) return null;
    return decimalHours;
  }
}

export function HHMMToDecimalRounded(timeString: string | null | undefined): number | null {
  if (!timeString || !timeString.trim()) {
    return null;
  }
  const time = timeString.trim();
  let decimalHours: number;
  
  if (time.includes(':')) {
    const [hours, minutes] = time.split(':').map(part => parseInt(part, 10));
    if (isNaN(hours) || isNaN(minutes) || minutes < 0 || minutes >= 60) return null;
    decimalHours = hours + minutes / 60;
  } else {
    decimalHours = parseFloat(time);
    if (isNaN(decimalHours)) return null;
  }
  
  // Round up to nearest 15-minute increment (0.25 hours)
  return Math.ceil(decimalHours * 4) / 4;
}

export function getSuggestedDayRateThreshold(
  roles: RoleInfo[],
  minimumBillableHours?: number | null,
  existingMultipliers?: RateMultiplier[]
): number | null {
  const anyDayRate = roles.some(role => role.billingStructure === 'day_rate');
  if (!anyDayRate) return null;

  const nonStraightTimeMultipliers = existingMultipliers?.filter(m => m.code !== 'STRAIGHT_TIME') || [];
  const dayRateThresholds = nonStraightTimeMultipliers
    .map(m => m.defaultPerShiftThresholdHours)
    .map(h => typeof h === 'string' ? parseFloat(h) : h)
    .filter((h): h is number => typeof h === 'number' && !isNaN(h) && h >= 0);

  if (dayRateThresholds.length === 0) {
    return minimumBillableHours ?? 8;
  } else {
    return Math.max(...dayRateThresholds) + 2;
  }
}

export function getSuggestedWeeklyThreshold(
  _roles: RoleInfo[],
  existingMultipliers?: RateMultiplier[]
): number | null {
  const nonStraightTimeMultipliers = existingMultipliers?.filter(m => m.code !== 'STRAIGHT_TIME') || [];
  const weeklyThresholds = nonStraightTimeMultipliers
    .map(m => m.defaultPerWeekThresholdHours)
    .map(h => typeof h === 'string' ? parseFloat(h) : h)
    .filter((h): h is number => typeof h === 'number' && !isNaN(h) && h >= 0);

  if (weeklyThresholds.length === 0) {
    return 40;
  } else {
    return Math.max(...weeklyThresholds) + 10;
  }
}

export const getDefaultNewMultiplier = (): Partial<RateMultiplier> => {
  return {
    name: '',
    defaultMultiplier: 1.5,
    defaultPerShiftThresholdHours: null,
    defaultPerWeekThresholdHours: null,
    perShiftBillingStructures: [],
    perWeekBillingStructures: [],
  };
};

export const isStraightTimeMultiplier = (code?: string | null) => code === 'STRAIGHT_TIME';

export const MULTIPLIER_SUGGESTIONS = [
  { name: 'Overtime', rate: 1.5 },
  { name: 'Double Time', rate: 2.0 },
  { name: 'Triple Time', rate: 3.0 },
];

export function generateMultiplierCode(candidateName: string, existingMultipliers: RateMultiplier[]): string {
  const base = candidateName
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 10);
  const existingCodes = new Set(existingMultipliers.map((m) => m.code));
  if (!existingCodes.has(base)) return base;
  let counter = 2;
  while (existingCodes.has(`${base}${counter}`)) counter++;
  return `${base}${counter}`;
}

